import { TFunction } from 'next-i18next'

import { FeatureType, ModuleType } from '../constants/template-info'
import { addGlobalMessage } from '../organisms/appLayout/molecules/globalCommunicationWrapper/GlobalCommunicationWrapper'
import { TOAST_TYPES } from '../organisms/appLayout/molecules/globalCommunicationWrapper/globalCommunicationWrapper.types'
import { isHttpClientError } from '../utils/HttpClient'
import isBrowser from '../utils/isBrowser'
import { captureException } from '../utils/sentryUtil'

import QuinceApiError, { isQuinceApiError } from './quince-api-error'

export interface QuinceServicesErrorResponse {
  errorCode: string
  message: string
  dateTime: string
}

// @i18n-ignore
const STANDARD_ERROR_MESSAGE_ENGLISH = 'Something went wrong please try again later'

class QuinceError extends Error {
  displayMessage: string
  parentError?: Error

  constructor(message: string, displayMessage: string, parentError?: Error) {
    super(message)
    this.displayMessage = displayMessage
    this.parentError = parentError
    return {
      ...this,
    }
  }

  static captureDataException = (
    error: Error | QuinceApiError,
    errorInfo: Record<string, any> | undefined = {},
    errorTags: Record<string, any> | undefined = {},
    module: ModuleType,
    feature: FeatureType,
  ) => {
    if (isBrowser()) {
      captureException(error, {
        module: module,
        feature,
        additionalTags: errorTags,
        extraInfo: errorInfo,
      })
    } else {
      throw error
    }
  }

  static fromError(error: Error | QuinceApiError, fallBackMessage?: string, t?: TFunction) {
    let httpError = error
    if (isQuinceApiError(error)) {
      httpError = error.apiErrorObject
    }

    const STANDARD_ERROR_MESSAGE = t ? t('something_went_wrong_please_try_again_later') : STANDARD_ERROR_MESSAGE_ENGLISH

    const defaultError = fallBackMessage || STANDARD_ERROR_MESSAGE
    if (!isHttpClientError(httpError)) {
      return new QuinceError(defaultError, defaultError, error)
    }
    const errorMessage = httpError.response?.data?.message || defaultError

    return new QuinceError(errorMessage, errorMessage, error)
  }

  static toastFromError(
    error: Error,
    options: {
      fallBackMessage?: string
      module: ModuleType
      feature: FeatureType
      t?: TFunction
    },
  ) {
    const { fallBackMessage, module, feature } = options || {}
    const quinceError = QuinceError.fromError(error, fallBackMessage, options.t)

    QuinceError.toastFromQuinceError(quinceError)
    captureException(error, {
      module,
      feature,
    })
  }

  static toastFromQuinceError(error: QuinceError, t?: TFunction) {
    addGlobalMessage({
      title: t ? t('error') : 'Error',
      description: error.displayMessage,
      type: TOAST_TYPES.error,
    })
  }
}

export default QuinceError
